import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Form, FormikProvider, FieldArray, useFormik } from 'formik';
import {
  Box,
  Card,
  Grid,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Button,
  Divider,
} from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import Autocomplete from '@material-ui/lab/Autocomplete';
import fakeRequest from 'src/utils/fakeRequest';
import useAuth from 'src/hooks/useAuth';
import Icon from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';
import plusFill from '@iconify/icons-eva/plus-fill';
import { REST_API } from 'src/constants/DefaultValues';
import { GOOGLE_MAPS_API_KEY } from 'src/DefaultValues';
import axios from 'axios';
import { indianMobileSchema, normalizeMobile } from 'src/utils/authIdentifier';

const emptyAddress = (index = 0) => ({
  id: undefined,
  label: `Address ${index + 1}`,
  address: '',
  landmark: '',
  city: '',
  state: '',
  postalCode: '',
  latitude: null,
  longitude: null,
});

const loadGoogleMapsScript = (apiKey) => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve(window.google);
      return;
    }
    const existingScript = document.getElementById('google-maps-places-script');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google));
      existingScript.addEventListener('error', (e) => reject(e));
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-places-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
};

const parsePlaceDetails = (placeResult, fallbackName) => {
  const components = placeResult?.address_components || [];
  let city = '';
  let state = '';
  let postalCode = '';
  let route = '';
  let sublocality = '';

  components.forEach((c) => {
    const types = c.types || [];
    if (types.some((t) => t.includes('postal_code'))) {
      postalCode = c.long_name || c.short_name;
    } else if (types.includes('administrative_area_level_1')) {
      state = c.long_name;
    } else if (types.includes('locality')) {
      city = c.long_name;
    } else if (!city && (types.includes('administrative_area_level_2') || types.includes('postal_town'))) {
      city = c.long_name;
    } else if (!city && (types.includes('sublocality_level_1') || types.includes('sublocality'))) {
      city = c.long_name;
    }

    if (types.includes('route')) {
      route = c.long_name;
    }
    if (types.includes('sublocality_level_1') || types.includes('sublocality') || types.includes('neighborhood')) {
      sublocality = c.long_name;
    }
  });

  if (!postalCode) {
    const textToSearch = [placeResult?.formatted_address, fallbackName].filter(Boolean).join(' ');
    const pinMatch = textToSearch.match(/\b([1-9][0-9]{5})\b/);
    if (pinMatch) {
      postalCode = pinMatch[1];
    }
  }

  const lat = placeResult?.geometry?.location?.lat
    ? (typeof placeResult.geometry.location.lat === 'function' ? placeResult.geometry.location.lat() : placeResult.geometry.location.lat)
    : null;
  const lng = placeResult?.geometry?.location?.lng
    ? (typeof placeResult.geometry.location.lng === 'function' ? placeResult.geometry.location.lng() : placeResult.geometry.location.lng)
    : null;

  return {
    landmark: fallbackName || placeResult?.name || placeResult?.formatted_address || '',
    address: [route, sublocality].filter(Boolean).join(', ') || placeResult?.formatted_address || '',
    city,
    state,
    postalCode: postalCode ? String(postalCode).replace(/\D/g, '').slice(0, 6) : '',
    latitude: lat,
    longitude: lng,
  };
};

const mapAddressesFromUser = (currentUser) => {
  if (Array.isArray(currentUser?.addresses) && currentUser.addresses.length) {
    return currentUser.addresses.map((a, i) => ({
      id: a.id || undefined,
      label: a.label || `Address ${i + 1}`,
      address: a.address || '',
      landmark: a.landmark || '',
      city: a.city || '',
      state: a.state || '',
      postalCode: a.postal_code || a.postalCode || '',
      latitude: a.latitude ?? null,
      longitude: a.longitude ?? null,
    }));
  }
  if (currentUser?.address || currentUser?.city) {
    return [{
      id: undefined,
      label: 'Address 1',
      address: currentUser.address || '',
      landmark: currentUser.landmark || '',
      city: currentUser.city || '',
      state: currentUser.state || '',
      postalCode: currentUser.postal_Code || currentUser.postalCode || '',
      latitude: currentUser.latitude ?? null,
      longitude: currentUser.longitude ?? null,
    }];
  }
  return [emptyAddress(0)];
};

MayuraReceiverAddEditForm.propTypes = {
  isEdit: PropTypes.bool,
  currentUser: PropTypes.object,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default function MayuraReceiverAddEditForm({ isEdit, currentUser, onClose, onSuccess }) {
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [branchList, setBranchList] = useState([]);
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const taxTypeOptions = ['GST', 'URP'];
  const [landmarkOptionsByIndex, setLandmarkOptionsByIndex] = useState({});
  const [landmarkInputByIndex, setLandmarkInputByIndex] = useState({});
  const [activeLandmarkIndex, setActiveLandmarkIndex] = useState(null);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${REST_API}branches/get-all-active-branches`);
      setBranchList(res.data.status === 1 ? res.data.data : []);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranchList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    const apiKey = GOOGLE_MAPS_API_KEY || 'AIzaSyD1VmydeASm4dawh3VO8b-lYlvM5exe4NY';
    loadGoogleMapsScript(apiKey).catch((err) => console.error('Error loading Google Maps script:', err));
  }, []);

  useEffect(() => {
    const initial = mapAddressesFromUser(currentUser);
    const inputs = {};
    initial.forEach((a, i) => {
      inputs[i] = a.landmark || '';
    });
    setLandmarkInputByIndex(inputs);
  }, [currentUser]);

  useEffect(() => {
    if (activeLandmarkIndex === null) return;
    const landmarkInputValue = landmarkInputByIndex[activeLandmarkIndex] || '';
    if (!landmarkInputValue || landmarkInputValue.trim().length < 2) {
      setLandmarkOptionsByIndex((prev) => ({ ...prev, [activeLandmarkIndex]: [] }));
      return;
    }

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }

    const autocompleteService = new window.google.maps.places.AutocompleteService();
    autocompleteService.getPlacePredictions(
      {
        input: landmarkInputValue,
        componentRestrictions: { country: 'in' },
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setLandmarkOptionsByIndex((prev) => ({ ...prev, [activeLandmarkIndex]: predictions }));
        } else {
          setLandmarkOptionsByIndex((prev) => ({ ...prev, [activeLandmarkIndex]: [] }));
        }
      }
    );
  }, [landmarkInputByIndex, activeLandmarkIndex]);

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const addressSchema = Yup.object().shape({
    label: Yup.string().trim().required('Address name is required'),
    address: Yup.string().trim().required('Address is required'),
    landmark: Yup.string().trim().required('Landmark is required'),
    city: Yup.string().trim().required('City is required'),
    state: Yup.string().trim().required('State is required'),
    postalCode: Yup.string()
      .matches(/^[0-9]{5,10}$/, 'Only digits between 5 to 10 length')
      .nullable(),
  });

  const ReceiverSchema = Yup.object().shape({
    name: Yup.string()
      .matches(/^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/, 'Only alphanumeric characters and single spaces allowed')
      .required('Name is required'),
    taxType: Yup.string()
      .oneOf(['GST', 'URP'], 'Invalid tax type')
      .required('Tax type is required'),
    gst: Yup.string().when('taxType', {
      is: 'GST',
      then: Yup.string()
        .matches(/^[A-Z0-9]{1,15}$/, 'Only uppercase letters and numbers allowed, max 15 chars')
        .required('GST No is required'),
      otherwise: Yup.string().nullable(),
    }),
    urp: Yup.string().when('taxType', {
      is: 'URP',
      then: Yup.string()
        .matches(/^[A-Z0-9]{1,15}$/, 'Only uppercase letters and numbers allowed, max 15 chars')
        .required('URP No is required'),
      otherwise: Yup.string().nullable(),
    }),
    mobile: indianMobileSchema('Mobile No is required'),
    branchLocation: Yup.object().nullable().required('Branch is required'),
    landline: Yup.string()
      .transform((value) => (value === '' ? undefined : value))
      .notRequired()
      .matches(/^[0-9]{6,12}$/, {
        message: 'Only digits, min 6 and max 12 digits',
        excludeEmptyString: true,
      }),
    email: Yup.string().email('Invalid email format').required('Email is required').nullable(false),
    password: isEdit
      ? Yup.string()
          .transform((value) => (value === '' ? undefined : value))
          .notRequired()
          .min(8, 'Password must be at least 8 characters')
          .max(30, 'Password must not exceed 30 characters')
          .matches(
            /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
            {
              message: 'Must include uppercase, lowercase, number, and special character',
              excludeEmptyString: true,
            }
          )
      : Yup.string()
          .min(8, 'Password must be at least 8 characters')
          .max(30, 'Password must not exceed 30 characters')
          .matches(
            /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
            'Must include uppercase, lowercase, number, and special character'
          )
          .required('Password is required'),
    addresses: Yup.array().of(addressSchema).min(1, 'At least one address is required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      customer_id: currentUser?.customer_id || '',
      name: currentUser?.user_name || '',
      mobile: currentUser?.phone_number || '',
      taxType: currentUser?.urp ? 'URP' : 'GST',
      gst: currentUser?.gst || '',
      urp: currentUser?.urp || '',
      branchLocation: currentUser?.branch_id
        ? branchList.find((branch) => branch.id === currentUser.branch_id) || null
        : null,
      landline: currentUser?.land_line ?? currentUser?.landline ?? '',
      email: currentUser?.email || '',
      password: currentUser?.password_view || '',
      isSelf: currentUser?.is_self || false,
      customer_type: 2,
      addresses: mapAddressesFromUser(currentUser),
    },
    validationSchema: ReceiverSchema,
    onSubmit: async (values, { setSubmitting, resetForm, setErrors }) => {
      try {
        await fakeRequest(500);

        const primary = values.addresses[0] || {};
        const payload = {
          ...values,
          address: primary.address,
          landmark: primary.landmark,
          city: primary.city,
          state: primary.state,
          postalCode: primary.postalCode,
          latitude: primary.latitude,
          longitude: primary.longitude,
          addresses: values.addresses.map((a, i) => ({
            id: a.id,
            label: a.label || `Address ${i + 1}`,
            address: a.address,
            landmark: a.landmark,
            city: a.city,
            state: a.state,
            postalCode: a.postalCode,
            latitude: a.latitude,
            longitude: a.longitude,
          })),
          addedBy: user?.id,
        };

        if (isEdit && !(values.password && String(values.password).trim())) {
          delete payload.password;
        }

        if (isEdit && currentUser?.user_id) {
          payload.user_id = currentUser.user_id;
          payload.customer_id = values.customer_id;
          payload.updated_by = user?.id;
        }

        const apiEndpoint = isEdit
          ? `${REST_API}customers/update-customer`
          : `${REST_API}customers/add-customer`;

        const res = await axios.post(apiEndpoint, payload);
        if (res.data.status === 1) {
          enqueueSnackbar(isEdit ? 'Receiver updated successfully' : 'Receiver created successfully', { variant: 'success' });
          resetForm();
          setSubmitting(false);
          if (!isEdit && onSuccess) {
            const createdReceiver = res.data.data?.id
              ? res.data.data
              : values.mobile
                ? { phone_number: normalizeMobile(values.mobile) }
                : null;
            await onSuccess(createdReceiver);
          } else if (onClose) {
            onClose();
          }
        } else if (res.data.status === 2) {
          enqueueSnackbar(res.data.message || 'No changes were made to the receiver profile', { variant: 'info' });
          setSubmitting(false);
          onClose();
        } else {
          const message = res.data.message || 'Operation failed';
          enqueueSnackbar(
            /phone number already exist/i.test(message) ? 'Phone number already exist' : message,
            { variant: 'error' }
          );
          setSubmitting(false);
        }
      } catch (error) {
        console.error(error);
        setSubmitting(false);
        const message = error.response?.data?.message || error.message || 'Operation failed';
        enqueueSnackbar(
          /phone number already exist/i.test(message) ? 'Phone number already exist' : message,
          { variant: 'error' }
        );
        setErrors(error);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    setFieldTouched,
    isSubmitting,
  } = formik;

  const applyPlaceToAddress = useCallback(
    (index, parsed, currentAddressValue) => {
      if (parsed.landmark) {
        setFieldValue(`addresses[${index}].landmark`, parsed.landmark);
        setLandmarkInputByIndex((prev) => ({ ...prev, [index]: parsed.landmark }));
      }
      if (parsed.city) setFieldValue(`addresses[${index}].city`, parsed.city);
      if (parsed.state) setFieldValue(`addresses[${index}].state`, parsed.state);
      if (parsed.postalCode) setFieldValue(`addresses[${index}].postalCode`, parsed.postalCode);
      if (parsed.latitude && parsed.longitude) {
        setFieldValue(`addresses[${index}].latitude`, parsed.latitude);
        setFieldValue(`addresses[${index}].longitude`, parsed.longitude);
      }
      if (parsed.address && !currentAddressValue) {
        setFieldValue(`addresses[${index}].address`, parsed.address);
      }
    },
    [setFieldValue]
  );

  const handleLandmarkSelect = (index, selectedValue) => {
    if (!selectedValue) return;
    if (typeof selectedValue === 'object' && selectedValue.place_id) {
      const mainText = selectedValue.structured_formatting?.main_text || selectedValue.description;
      setFieldValue(`addresses[${index}].landmark`, mainText);
      setLandmarkInputByIndex((prev) => ({ ...prev, [index]: mainText }));
      setFieldTouched(`addresses[${index}].landmark`, true, false);

      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ placeId: selectedValue.place_id }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const placeDesc = selectedValue.description || selectedValue.structured_formatting?.secondary_text || mainText;
            let parsed = parsePlaceDetails(results[0], placeDesc);

            if (!parsed.postalCode) {
              for (const res of results) {
                const temp = parsePlaceDetails(res, placeDesc);
                if (temp.postalCode) {
                  parsed.postalCode = temp.postalCode;
                  break;
                }
              }
            }

            if (!parsed.postalCode && selectedValue) {
              const descText = selectedValue.description || selectedValue.structured_formatting?.secondary_text || '';
              const match = descText.match(/\b([1-9][0-9]{5})\b/);
              if (match) parsed.postalCode = match[1];
            }

            applyPlaceToAddress(index, parsed, values.addresses[index]?.address);

            if (!parsed.postalCode && parsed.latitude && parsed.longitude) {
              geocoder.geocode(
                { location: { lat: parsed.latitude, lng: parsed.longitude } },
                (revResults, revStatus) => {
                  let foundPostalCode = '';
                  if (revStatus === 'OK' && revResults && revResults.length > 0) {
                    for (const res of revResults) {
                      const revParsed = parsePlaceDetails(res);
                      if (revParsed.postalCode) {
                        foundPostalCode = revParsed.postalCode;
                        break;
                      }
                    }
                  }
                  if (foundPostalCode) {
                    setFieldValue(`addresses[${index}].postalCode`, foundPostalCode);
                  }
                }
              );
            }
          }
        });
      }
    } else if (typeof selectedValue === 'string') {
      setFieldValue(`addresses[${index}].landmark`, selectedValue);
      setLandmarkInputByIndex((prev) => ({ ...prev, [index]: selectedValue }));
      setFieldTouched(`addresses[${index}].landmark`, true, false);
    }
  };

  const addressErrors = Array.isArray(errors.addresses) ? errors.addresses : [];
  const addressTouched = Array.isArray(touched.addresses) ? touched.addresses : [];

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Card sx={{ p: 3, boxShadow: 'none' }}>
          <Stack gap={2} direction="column">
            <Box sx={{ pl: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isSelf}
                    onChange={(e) => setFieldValue('isSelf', e.target.checked)}
                  />
                }
                label="Self"
              />
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField fullWidth value="Consignee" InputProps={{ readOnly: true }} />
              <TextField
                fullWidth
                label="Name"
                {...getFieldProps('name')}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
              <TextField
                fullWidth
                label="Mobile No"
                {...getFieldProps('mobile')}
                error={touched.mobile && Boolean(errors.mobile)}
                helperText={touched.mobile && errors.mobile}
              />
              <TextField
                fullWidth
                label="Landline No"
                autoComplete="tel-national"
                {...getFieldProps('landline')}
                error={touched.landline && Boolean(errors.landline)}
                helperText={touched.landline && errors.landline}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Email"
                {...getFieldProps('email')}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...getFieldProps('password')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleShowPassword} edge="end">
                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />
              <Autocomplete
                fullWidth
                options={taxTypeOptions}
                value={values.taxType || 'GST'}
                onChange={(_, newValue) => {
                  setFieldValue('taxType', newValue || 'GST');
                  if (newValue === 'GST') setFieldValue('urp', '');
                  else if (newValue === 'URP') setFieldValue('gst', '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tax Type"
                    error={touched.taxType && Boolean(errors.taxType)}
                    helperText={touched.taxType && errors.taxType}
                  />
                )}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {values.taxType === 'GST' ? (
                <TextField
                  fullWidth
                  label="GST No"
                  {...getFieldProps('gst')}
                  error={touched.gst && Boolean(errors.gst)}
                  helperText={touched.gst && errors.gst}
                />
              ) : (
                <TextField
                  fullWidth
                  label="URP No"
                  {...getFieldProps('urp')}
                  error={touched.urp && Boolean(errors.urp)}
                  helperText={touched.urp && errors.urp}
                />
              )}
              <Autocomplete
                options={branchList}
                fullWidth
                loading={loading}
                getOptionLabel={(option) => `${option.branch_address} - ${option.branch_code}`}
                value={values.branchLocation}
                onChange={(_, newValue) => setFieldValue('branchLocation', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Branch Added"
                    fullWidth
                    error={touched.branchLocation && Boolean(errors.branchLocation)}
                    helperText={touched.branchLocation && errors.branchLocation}
                  />
                )}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />

            <FieldArray name="addresses">
              {({ push, remove }) => (
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6">Receiver Addresses</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add one or more delivery addresses for this receiver.
                      </Typography>
                    </Box>
                    <Button
                      startIcon={<Icon icon={plusFill} />}
                      onClick={() => {
                        const nextIndex = values.addresses.length;
                        push(emptyAddress(nextIndex));
                        setLandmarkInputByIndex((prev) => ({ ...prev, [nextIndex]: '' }));
                      }}
                    >
                      Add Another Address
                    </Button>
                  </Stack>

                  {typeof errors.addresses === 'string' && (
                    <Typography color="error" variant="caption">{errors.addresses}</Typography>
                  )}

                  {values.addresses.map((addr, index) => {
                    const err = addressErrors[index] || {};
                    const touch = addressTouched[index] || {};
                    return (
                      <Card
                        key={addr.id || `new-${index}`}
                        variant="outlined"
                        sx={{ p: 2, bgcolor: 'background.neutral' }}
                      >
                        <Stack spacing={2}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <TextField
                              label="Address name"
                              value={addr.label}
                              onChange={(e) => setFieldValue(`addresses[${index}].label`, e.target.value)}
                              error={Boolean(touch.label && err.label)}
                              helperText={touch.label && err.label}
                              sx={{ maxWidth: 280 }}
                            />
                            {values.addresses.length > 1 && (
                              <IconButton color="error" onClick={() => remove(index)}>
                                <Icon icon={trash2Outline} />
                              </IconButton>
                            )}
                          </Stack>

                          <TextField
                            fullWidth
                            label="Address"
                            {...getFieldProps(`addresses[${index}].address`)}
                            error={Boolean(touch.address && err.address)}
                            helperText={touch.address && err.address}
                          />

                          <Autocomplete
                            freeSolo
                            fullWidth
                            options={landmarkOptionsByIndex[index] || []}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') return option;
                              return option.description || '';
                            }}
                            inputValue={landmarkInputByIndex[index] || ''}
                            onFocus={() => setActiveLandmarkIndex(index)}
                            onInputChange={(event, newInputValue) => {
                              setActiveLandmarkIndex(index);
                              setLandmarkInputByIndex((prev) => ({ ...prev, [index]: newInputValue }));
                              setFieldValue(`addresses[${index}].landmark`, newInputValue);
                            }}
                            onChange={(event, selectedValue) => handleLandmarkSelect(index, selectedValue)}
                            value={addr.landmark || ''}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                label="Landmark (Search from Google Maps)"
                                error={Boolean(touch.landmark && err.landmark)}
                                helperText={touch.landmark && err.landmark}
                              />
                            )}
                          />

                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                              fullWidth
                              label="City"
                              {...getFieldProps(`addresses[${index}].city`)}
                              InputProps={{ readOnly: true }}
                              error={Boolean(touch.city && err.city)}
                              helperText={touch.city && err.city}
                            />
                            <TextField
                              fullWidth
                              label="State"
                              {...getFieldProps(`addresses[${index}].state`)}
                              InputProps={{ readOnly: true }}
                              error={Boolean(touch.state && err.state)}
                              helperText={touch.state && err.state}
                            />
                            <TextField
                              fullWidth
                              label="Postal Code"
                              {...getFieldProps(`addresses[${index}].postalCode`)}
                              InputProps={{ readOnly: true }}
                              error={Boolean(touch.postalCode && err.postalCode)}
                              helperText={touch.postalCode && err.postalCode}
                            />
                          </Stack>
                        </Stack>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </FieldArray>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <LoadingButton
                  sx={{ boxShadow: 'none' }}
                  startIcon={
                    <Stack
                      className={isEdit ? 'ic--twotone-edit' : 'lets-icons--add-duotone'}
                      sx={{ width: 24, height: 24 }}
                    />
                  }
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  {isEdit ? 'Update Receiver' : 'Add Receiver'}
                </LoadingButton>
              </Stack>
            </Grid>
          </Stack>
        </Card>
      </Form>
    </FormikProvider>
  );
}
